import { AgendaItem } from "../../../services/AgendaService";
import ClientService from "../../../services/ClientService";
import { TranslationService } from "../../../services/TranslationService";
import { Client } from "../../client/entities/ClientGetResponse";
import { EmailCompose } from "../../client/components/EmailCompose";
import NewComment from "../../client/activity/components/NewComment";
import NewTaskForm from "../../task/components/NewTaskForm";
import { TaskEdit } from "../../task/entities/TaskEdit";
import CompanyService from "../../../services/CompanyService";
import { openWhatsappPanel } from "../../../components/whatsapp/utils/openWhatsappPanel";
import { parseDate } from "../../../utils/ParseUtils";
import FloatingPanelService from "../../shared/FloatingPanel";
import { ToastService } from "../../shared/bootstrap/Toast";
import { AgendaAction } from "../../../entities/agenda/Agenda";
import { plainTextToHtml } from "./agendaUtils";

export interface AgendaFlowRequest {
  item: AgendaItem;
  /** Edited email subject (only meaningful for email). */
  subject: string;
  /** Edited plain text draft. */
  content: string;
  /** Called only after the flow saved successfully. */
  onSaved: () => void;
}

const loadClient = async (clientId: number): Promise<Client | undefined> => {
  const result = await ClientService.get({ id: clientId.toString() });
  if (result instanceof Error || !result.item) {
    ToastService.showToast(
      TranslationService.translate.AgendaFlowError,
      undefined,
      "danger"
    );
    return undefined;
  }
  return result.item;
};

const openEmail = async ({
  item,
  subject,
  content,
  onSaved
}: AgendaFlowRequest) => {
  const client = await loadClient(item.clienteId);
  if (!client) {
    return;
  }
  FloatingPanelService.showPanel({
    title: TranslationService.translate.EmailToClient,
    children: (
      <EmailCompose
        personId={item.clienteId.toString()}
        defaultTo={client.Email}
        defaultSubject={subject}
        defaultContent={plainTextToHtml(content)}
        onSubmit={onSaved}
      />
    ),
    width: 900,
    height: 669
  });
};

const openWhatsapp = async ({ item, content, onSaved }: AgendaFlowRequest) => {
  const client = await loadClient(item.clienteId);
  if (!client) {
    return;
  }
  await openWhatsappPanel({
    client,
    isNewConversation: true,
    initialMessage: content,
    onMessageSent: onSaved
  });
};

const openTask = async ({ item, content, onSaved }: AgendaFlowRequest) => {
  const task = new TaskEdit(
    CompanyService.getUserid()?.toString(),
    item.clienteId.toString()
  );
  task.description = content;
  // fechaSugerida is a calendar day: do not apply the company timezone.
  const dueDate = item.fechaSugerida
    ? parseDate(item.fechaSugerida.substring(0, 10))
    : undefined;
  if (dueDate) {
    task.dueDate = dueDate.toString();
    task.reminderDay = dueDate.toString();
  }
  FloatingPanelService.showPanel({
    title: TranslationService.translate.NewTask,
    children: (
      <NewTaskForm
        personId={item.clienteId.toString()}
        personName={item.nombre ?? ""}
        initialTask={task}
        onNewTask={onSaved}
      />
    )
  });
};

/** Call, meeting and escalation are logged as a client comment (activity). */
const openComment = ({ item, content, onSaved }: AgendaFlowRequest) => {
  FloatingPanelService.showPanel({
    title: TranslationService.translate.NewComment,
    children: (
      <NewComment
        clientid={item.clienteId.toString()}
        defaultContent={content}
        reload={onSaved}
      />
    ),
    width: 800,
    height: 700
  });
};

/**
 * Opens the existing app flow for the suggested action, prefilled with the
 * (edited) draft. `onSaved` runs only when the flow saved successfully.
 * Returns false when the action has no flow (unknown action).
 */
export async function openAgendaActionFlow(
  request: AgendaFlowRequest
): Promise<boolean> {
  switch (request.item.accion) {
    case AgendaAction.Email:
      await openEmail(request);
      return true;
    case AgendaAction.Whatsapp:
      await openWhatsapp(request);
      return true;
    case AgendaAction.Tarea:
      await openTask(request);
      return true;
    case AgendaAction.Llamada:
    case AgendaAction.Reunion:
    case AgendaAction.Escalar:
      openComment(request);
      return true;
    default:
      return false;
  }
}
